'use client';

import { useTranslations } from 'next-intl';
import { CacheKeys, ServerActionName } from '@headless-workspace/config';
import { AsyncData, ResultEmpty, ResultFailure } from '@headless-workspace/core-domain';
import { CommonDI, UserSessionRepositoryType } from '../../../DI';
import { getUserInfoServerAction, UserInfo, UserInfoErrors } from '../../../server';
import { RevalidationStrategy, useSafeSWR } from '../hooks';
import { useUserSession } from './useUserSession';

type UseUserInfoReturnType = AsyncData<UserInfo>;

export const useUserInfo = (): UseUserInfoReturnType => {
    const t = useTranslations('Common.user.error');

    const { isLoading: isUserSessionLoading } = useUserSession(CommonDI.get(UserSessionRepositoryType));

    const {
        data,
        isLoading: isUserInfoLoading,
        error,
    } = useSafeSWR(
        CacheKeys.User,
        ServerActionName.getUserInfo,
        () => getUserInfoServerAction(),
        RevalidationStrategy.HotDataPolicy,
    );

    const isLoading = isUserSessionLoading || isUserInfoLoading;

    if (!data) {
        if (error) {
            return {
                isLoading,
                result: ResultFailure(
                    UserInfoErrors.Default,
                    error instanceof Error ? error.message : t('defaultError'),
                ),
            };
        }

        return { isLoading, result: ResultEmpty() };
    }

    return { isLoading, result: data };
};
